<template>
  <div
    :class="_class"
    v-on="_itemEvents"
  >
    <div
      :class="_contentClass"
      v-on="_contentEvents"
    >
      <a
        v-bind="_anchorAttr"
        v-if="_anchorAttr"
        ref="anchor"
      />
      <div
        class="ten-menu-item__contentinner"
        :style="_customStyle"
        v-on="_contentInnerEvents"
      >
        <span
          class="ten-menu-item__content-icon"
          v-if="icon"
        >
          <!-- 菜单项 icon -->
          <slot name="icon">
            <ten-icon :icon="icon" />
          </slot>
        </span>
        <span class="ten-menu-item__content-content">
          <!-- 菜单项内容 -->
          <slot />
        </span>
        <ten-icon
          class="ten-menu-item__content-carret"
          icon="carret_down"
          v-if="isMenu"
        />
      </div>
    </div>
    <ten-slide-down
      v-if="isMenu"
      :disabled="_isCollapsed && deep === 0"
    >
      <div
        class="ten-menu-item__submenu"
        v-show="_isRealExpanded"
      >
        <slot name="children" />
      </div>
    </ten-slide-down>
  </div>
</template>

<script>
import TenIcon from '../icon';
import TenSlideDown from '../scripts/components/slide-down';
import TreeNodeModel from '../tree/tree-node-model';

const name = 'ten-menu-item';
let menuItemKey = 0;

export default {
  name,
  components: { TenIcon, TenSlideDown },
  inject: ['menu', 'treeNodeParent'],
  provide() {
    return {
      treeNodeParent: this,
    };
  },
  props: {
    /**
     * @ignore
     */
    isMenu: Boolean,
    /**
     * 菜单项头部图标，mode 为 vertical 时，需要提供以作折叠时显示
     */
    icon: String,
    /**
     * 菜单项唯一标识，如果不提供此属性，则该菜单项不会被激活
     */
    index: [Number, String],
    /**
     * Vue Router 路径对象
     */
    route: [Object, String],
    /**
     * 外部链接，如果提供此属性，将会模拟普通 a 标签点击行为，不会激活。同时支持其他 a 标签属性 (target, download, rel)
     */
    href: String,
  },
  data() {
    return {
      collapseExpanded: false,
      contentInnerHovered: false,
      status: {
        selected: 0,
        expanded: 0,
      },
      deep: 0,
    };
  },
  computed: {
    _isSelected() {
      return this.isMenu ? false : this.menu.active === this.index;
    },
    _isHalfSelected() {
      return this.isMenu ? this.status.selected !== 0 : false;
    },
    _class() {
      return [
        name,
        `${name}--mode-${this.menu.mode}`,
        `${name}--deep-${this.deep}`,
        `${name}--paddingdeep-${this._paddingDeep}`,
        {
          [`${name}--ismenu`]: this.isMenu,
          [`${name}--selected`]: this._isSelected,
          [`${name}--childrenselected`]: this._isHalfSelected,
          [`${name}--hoverexpanded`]: this._isCollapsedDeep0Submenu && this.collapseExpanded,
        },
      ];
    },
    _contentClass() {
      return [
        `${name}__content`,
        {
          [`${name}__content--selected`]: this._isSelected,
          [`${name}__content--childrenselected`]: this._isHalfSelected,
          [`${name}__content--ismenu`]: this.isMenu,
          [`${name}__content--expanded`]: this.status.expanded,
        },
      ];
    },
    _customStyle() {
      const { itemStyle, activeItemStyle, hoverItemStyle, activeSubItemStyle, activeCollapsedItemStyle } = this.menu;
      const caledActiveItemStyle = this._isCollapsedDeep0 ? activeCollapsedItemStyle : activeItemStyle;

      let customStyle = itemStyle;
      if (this._isSelected) {
        customStyle = caledActiveItemStyle;
      } else if (this.contentInnerHovered) {
        customStyle = hoverItemStyle;
      } else if (this.isMenu && this._isHalfSelected) {
        customStyle = activeSubItemStyle;
      }
      return customStyle;
    },
    _itemEvents() {
      if (this._isCollapsedDeep0Submenu && this.isMenu) {
        return {
          mouseenter: () => this.onMouseEnter(),
          mouseleave: () => this.onMouseLeave(),
        };
      }
      return null;
    },
    _contentEvents() {
      if (!this._isCollapsedDeep0Submenu) {
        return {
          click: () => this.onContentClick(),
        };
      }
      return null;
    },
    _contentInnerEvents() {
      const { hoverItemStyle } = this.menu;
      if (hoverItemStyle) {
        return {
          mouseover: () => this.onContentInnerMouseOver(),
          mouseout: () => this.onContentInnerMouseOut(),
        };
      }
      return null;
    },
    _isCollapsed() {
      return (
        this.menu.mode === 'horizontal'
        || (this.menu.mode === 'vertical' && this.menu.collapsed)
      );
    },
    _isCollapsedDeep0() {
      return (
        this.deep === 0 && this._isCollapsed
      );
    },
    _isCollapsedDeep0Submenu() {
      return (
        this._isCollapsedDeep0 && this.isMenu
      );
    },
    _paddingDeep() {
      return this._isCollapsed && this.deep > 0 ? this.deep - 1 : this.deep;
    },
    _isRealExpanded() {
      if (this._isCollapsedDeep0Submenu) {
        return this.collapseExpanded;
      }
      return this.status.expanded;
    },
    _anchorAttr() {
      if (this.href) {
        const { href } = this;
        const { target, download, rel } = this.$attrs;
        return {
          href, target, download, rel,
        };
      }
      return null;
    },
  },
  watch: {
    index: {
      handler() {
        const key = typeof this.index === 'undefined' ? (`tenMenuItem${menuItemKey}`) : this.index;
        menuItemKey += 1;
        this.nodeModel.setConfig({ key, isLeaf: !this.isMenu });
        this.nodeModel.setStatus(this.status);
        this.nodeModel.mount(this.treeNodeParent.nodeModel);
        this.deep = this.nodeModel.deep;
      },
      immediate: true,
    },
  },
  beforeCreate() {
    this.nodeModel = new TreeNodeModel();
  },
  created() {
    this.nodeModel.mount(this.treeNodeParent.nodeModel);
  },
  beforeDestroy() {
    this.nodeModel.unMount();
  },
  methods: {
    onContentClick() {
      if (this.isMenu) {
        this.handleMenuClick();
      } else {
        this.handleItemClick();
      }
    },

    // 子菜单点击
    handleMenuClick() {
      this.menu.onNodeExpandChange(this.nodeModel.key, this.status.expanded === 1 ? 0 : 1);
      // this.menu.toggleExpand(this);
    },

    // 菜单项点击
    handleItemClick() {
      if (this._anchorAttr && this.$refs.anchor) {
        this.$refs.anchor.click();
        return;
      }
      if (typeof this.index !== 'undefined') {
        this.menu.setActive(this);
      }
      // 快捷路由跳转
      if (this.route) {
        try {
          this.$router.push(
            this.route,
            () => {},
            () => {}
          );
        } catch (e) {
          console.error(e);
        }
      }
    },

    onMouseEnter() {
      this.collapseExpanded = true;
    },

    onMouseLeave() {
      this.collapseExpanded = false;
    },

    onContentInnerMouseOver() {
      this.contentInnerHovered = true;
    },

    onContentInnerMouseOut() {
      this.contentInnerHovered = false;
    },
  },
};
</script>
